import { CheckCircle2, XCircle } from "lucide-react";

import { ColocaCloseButton } from "@/shared/components/buttons/ColocaCloseButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import type { SettingsTranslations } from "@/shared/lang/interfaces/settings";
import { useTranslations } from "@/shared/lang/useTranslations";

import type {
  OrganizationWebhookHandshakeResult,
  OrganizationWebhookTestResult,
} from "../../services/organizationWebhooksService";

export interface WebhookTestResultAlertProps {
  result: OrganizationWebhookTestResult;
  /** The header the secret arrives in, quoted in the instructions. */
  secretHeader: string;
  onDismiss: () => void;
}

/**
 * What the handshake concluded, and what to change to fix it.
 *
 * Each outcome gets its own sentence because they need opposite actions: a `secret_missing` receiver is
 * running and reachable and only needs to echo the value back, while `no_response` means nothing is listening
 * at all. A single "the test failed" would leave the integrator guessing between them.
 */
export function WebhookTestResultAlert({ result, secretHeader, onDismiss }: WebhookTestResultAlertProps) {
  const { t } = useTranslations("settings");

  const detailKey: Record<OrganizationWebhookHandshakeResult, keyof SettingsTranslations> = {
    verified: "webhooks_handshake_verified_detail",
    no_response: "webhooks_handshake_no_response_detail",
    http_error: "webhooks_handshake_http_error_detail",
    invalid_response: "webhooks_handshake_invalid_response_detail",
    secret_missing: "webhooks_handshake_secret_missing_detail",
    secret_mismatch: "webhooks_handshake_secret_mismatch_detail",
  };

  const heading = result.success
    ? t("webhooks_handshake_verified_title", { ms: result.response_time_ms })
    : t(`webhooks_handshake_${result.handshake}_title` as keyof SettingsTranslations);

  const detail = t(detailKey[result.handshake], {
    header: secretHeader,
    status: result.status_code ?? "—",
    ms: result.response_time_ms,
    error: result.error_message ?? t("webhooks_no_response"),
  });

  return (
    <ColocaAlert
      variant={result.success ? "success" : "destructive"}
      icon={result.success ? CheckCircle2 : XCircle}
      heading={heading}
      controls={<ColocaCloseButton onClick={onDismiss} />}
    >
      <p className="text-[12px]">{detail}</p>

      {/* The contract, shown exactly where it is missing. */}
      {(result.handshake === "secret_missing" ||
        result.handshake === "invalid_response" ||
        result.handshake === "secret_mismatch") && (
        <pre className="mt-2 overflow-x-auto rounded bg-muted/60 p-2.5 font-mono text-[11.5px] leading-relaxed text-foreground">
          {`HTTP/1.1 200 OK\nContent-Type: application/json\n\n{ "secret": "<${secretHeader}>" }`}
        </pre>
      )}

      <p className="mt-1 text-[11.5px] opacity-80">{t("webhooks_test_hint")}</p>
    </ColocaAlert>
  );
}
